'use client'

import { useRef, useState } from 'react'
import { Button, Text } from '@/design-system'
import { AuthErrorMessage } from './AuthErrorMessage'

interface VerificationStepProps {
  email: string
  isLoading: boolean
  error: string | null
  onSubmit: (code: string) => void
  onResend: () => void
  onBack: () => void
}

export function VerificationStep({ email, isLoading, error, onSubmit, onResend, onBack }: VerificationStepProps) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  function fillFromIndex(index: number, chars: string) {
    const cleaned = chars.replace(/\D/g, '')
    if (!cleaned) return
    const next = [...digits]
    let i = index
    for (const ch of cleaned) {
      if (i > 5) break
      next[i] = ch
      i++
    }
    setDigits(next)

    const lastFilled = Math.min(i - 1, 5)
    const nextEmpty = next.findIndex((d) => d === '')
    const focusIdx = nextEmpty === -1 ? lastFilled : nextEmpty
    inputRefs.current[focusIdx]?.focus()

    if (next.every((d) => d !== '')) {
      onSubmit(next.join(''))
    }
  }

  function handleChange(index: number, value: string) {
    if (value.length > 1) {
      fillFromIndex(index, value)
      return
    }
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[index] = digit
    setDigits(next)

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    if (next.every((d) => d !== '')) {
      onSubmit(next.join(''))
    }
  }

  function handlePaste(index: number, e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text')
    if (!text) return
    e.preventDefault()
    fillFromIndex(index, text)
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 18,
          padding: '4px 8px',
          border: 'var(--border-thin) solid var(--ink)',
          background: 'var(--paper)',
          cursor: 'pointer'
        }}>
        <Text as="span" variant="tag">
          ← Back
        </Text>
      </button>
      <div style={{ textAlign: 'center', marginBottom: 6 }}>
        <Text as="div" variant="h3" weight={700} style={{ fontSize: 22, letterSpacing: '-0.02em' }}>
          Check your email
        </Text>
        <Text as="p" variant="body-sm" opacity={0.75} style={{ margin: '4px 0 0' }}>
          6-digit code sent to <span style={{ fontWeight: 700, opacity: 1 }}>{email}</span>
        </Text>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'center',
          margin: '18px 0 12px'
        }}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            disabled={isLoading}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={(e) => handlePaste(i, e)}
            aria-label={`Digit ${i + 1}`}
            style={{
              width: 40,
              height: 48,
              textAlign: 'center',
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontWeight: 700,
              border: 'var(--border-w) solid var(--ink)',
              background: digit ? 'var(--accent)' : 'var(--paper)',
              boxShadow: 'var(--shadow-sm)',
              outline: 'none',
              color: 'var(--ink)'
            }}
          />
        ))}
      </div>
      {error && (
        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          <AuthErrorMessage code={error} />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
        <Button
          variant="primary"
          onClick={() => {
            if (digits.every((d) => d !== '')) onSubmit(digits.join(''))
          }}
          disabled={isLoading || digits.some((d) => !d)}>
          {isLoading ? 'Verifying…' : 'Verify →'}
        </Button>
      </div>
      <Text as="p" variant="mono-sm" opacity={0.7} style={{ textAlign: 'center', margin: 0 }}>
        Didn&apos;t get it?{' '}
        <button
          type="button"
          onClick={onResend}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--ink)',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 700,
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: 0
          }}>
          Resend code
        </button>
      </Text>
    </div>
  )
}
